<template>
  <!-- DEV-11115: AI recommendations row, shared by the cart (DEV-11115) and the checkout page
       (DEV-11344). The parent gates it on its own store flag; this renders nothing at all
       when the AI service is off/empty/failed -- no error, no empty state. -->
  <!-- The row arrives 3-8s after the cart opens (LLM latency), so it fades and rises
       in rather than popping. Opacity + transform only -- animating height would
       jank the scrolling cart and shift the totals below mid-animation. -->
  <transition name="ai-cart-recs-fade">
    <div v-if="aiCartRecommendations.length" class="ai-cart-recs">
      <v-divider></v-divider>
      <div class="ai-cart-recs-head">
        <span class="ai-cart-recs-title">{{ aiRecommendationsTitle }}</span>
        <span class="ai-cart-recs-subtitle">{{ aiRecommendationsSubtitle }}</span>
      </div>
      <!-- During a refresh the existing cards stay put, dimmed, rather than being
           swapped for skeletons: they're still valid picks, and replacing them would
           make the row jump on every add. The section itself never renders before it
           has items, so there is no first-load skeleton to leave behind. -->
      <div class="ai-cart-recs-list" :class="{ 'ai-cart-recs-refreshing': getAiCartRecommendationsLoading }">
        <div
          v-for="item in aiCartRecommendations"
          :key="item.code"
          class="ai-cart-rec-card"
          @click="openAiRecommendationDialog(item)"
        >
          <div class="ai-cart-rec-pic">
            <img
              v-if="aiRecImage(item) && !aiRecImageErrors[item.code]"
              :src="aiRecImage(item)"
              :alt="item.name"
              @error="aiRecImageFailed(item)"
            />
            <!-- No image, or it failed to load: a neutral placeholder rather than the
                 browser's broken-image icon. -->
            <div v-else class="ai-cart-rec-pic-empty">
              <v-icon size="28" color="#bbb">restaurant</v-icon>
            </div>
            <v-btn
              class="ai-cart-rec-add"
              icon
              small
              :aria-label="$vuetify.t('$vuetify.cart.addToCart')"
              @click.stop="addAiRecommendation(item)"
            >
              <v-icon size="18">add</v-icon>
            </v-btn>
          </div>
          <div class="ai-cart-rec-body">
            <span v-if="item.aiTag" class="ai-tag-pill">{{ item.aiTag }}</span>
            <div class="ai-cart-rec-name">{{ item.name }}</div>
            <div v-if="item.aiReason" class="ai-cart-rec-reason">{{ item.aiReason }}</div>
            <div class="ai-cart-rec-price">
              <!-- "From" because a configurable item's real total depends on the
                   modifiers the guest picks in the dialog. -->
              <span v-if="aiRecNeedsConfig(item)">{{ $vuetify.t('$vuetify.cart.aiRecommendationsFrom') }} </span>{{ $currency(aiRecPrice(item), true, true) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { get, isEmpty, cloneDeep } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { matchTranslation } from '@/services/util.service'

export default {
  name: 'AiRecommendationsRow',

  data: () => ({
    // Codes whose recommendation image 404'd, so the card falls back to a placeholder
    // instead of the browser's broken-image icon.
    aiRecImageErrors: {}
  }),

  computed: {
    ...mapGetters([
      'isLoggedIn',
      'getAiCartRecommendations',
      'getAiCartRecommendationsLoading',
      'findRealMenuItemByCode',
      'getOriginalChargeFromCharges',
      'getFinalChargeFromCharges'
    ]),
    // aiTag/aiReason arrive as raw translations arrays from the Vuex getter (which can't
    // resolve them -- getters aren't reactive to $vuetify.lang.current, so a resolved
    // string would go stale on a language switch). Resolve here, same as StorePage.vue's
    // categoriesWithAiRecommendations does for the menu row.
    aiCartRecommendations() {
      return (this.getAiCartRecommendations || []).map(item => ({
        ...item,
        aiTag: matchTranslation(item.aiTagTranslations) || '',
        aiReason: matchTranslation(item.aiReasonTranslations) || ''
      }))
    },
    // "For You" / "Based on your orders" only holds for the logged-in /ai/upselling
    // path. A logged-out guest gets /ai/popular-items (anonymous best-sellers, not
    // personalized at all), so the copy must not claim otherwise -- same split the menu
    // category's title already makes.
    aiRecommendationsTitle() {
      return this.isLoggedIn
        ? this.$vuetify.t('$vuetify.cart.aiRecommendationsTitle')
        : this.$vuetify.t('$vuetify.cart.aiRecommendationsTitleGuest')
    },
    aiRecommendationsSubtitle() {
      return this.isLoggedIn
        ? this.$vuetify.t('$vuetify.cart.aiRecommendationsSubtitle')
        : this.$vuetify.t('$vuetify.cart.aiRecommendationsSubtitleGuest')
    }
  },

  methods: {
    ...mapActions(['addToCart']),

    aiRecImage(item) {
      // Item images come back as paths relative to the image host, not absolute URLs --
      // same prefixing rule menuItemCardCustom's getImage applies (an already-absolute
      // https:// url is used as-is).
      const imgPath = get(item, 'images.MAIN[0].url', '')
      if (!imgPath) return ''
      return imgPath.startsWith('https://') ? imgPath : `${process.env.VUE_APP_API_IMAGE}${imgPath}`
    },
    // A configurable item (a build-your-own pizza) has a base charge of 0 -- its real
    // cost is the cheapest required modifiers. Showing the base would render
    // "From $0.00". Same rule menuItemCardCustom's minimumRequiredPrice applies, using
    // the same shared charge getters so Pickup/Delivery overrides are respected.
    aiRecPrice(item) {
      const basePrice = get(this.getOriginalChargeFromCharges(item.charges || []), 'price', 0)
      if (basePrice) return basePrice

      const modifiers = get(item, 'attributes.Configurable', []) || []
      const cheapestRequired = modifiers
        .filter(mod => get(mod, 'minCardinality', 0) >= 1)
        .reduce((sum, mod) => {
          const values = (get(mod, 'values', []) || [])
            .map(v => get(this.getFinalChargeFromCharges(v.charges || []), 'price', 0))
            .sort((a, b) => a - b)
          if (!values.length) return sum
          // Cheapest way to satisfy the minimum: the cheapest option, that many times.
          return sum + values[0] * mod.minCardinality
        }, 0)

      return cheapestRequired || basePrice
    },
    // Vue 2 can't see a plain property added to an object, hence $set.
    aiRecImageFailed(item) {
      this.$set(this.aiRecImageErrors, item.code, true)
    },
    // An item with modifiers or required related items can't be added blind -- its real
    // price and validity depend on choices only the guest can make. Same shape the rest
    // of the app checks (attributes.Configurable / relations), per DEV-4546's commented
    // -out branch in menuItemCardCustom.vue.
    aiRecNeedsConfig(item) {
      const real = this.findRealMenuItemByCode(item.code) || item
      if (!isEmpty(get(real, 'attributes.Configurable', []))) return true
      return (get(real, 'relations', []) || []).some(rel => Number(get(rel, 'minCardinality', 0)) > 0)
    },
    // Tapping the card body opens the item detail page -- same as tapping a menu card.
    openAiRecommendationDialog(item) {
      // The AI card is a thin stand-in; open the REAL item by code so the dialog shows
      // its actual live modifiers/options (same approach as menuItemCardCustom's
      // handleOrderItem).
      const real = this.findRealMenuItemByCode(item.code)
      if (!real) return
      const clone = cloneDeep(real)
      if ('minCardinality' in clone) clone.qty = clone.minCardinality || 1
      this.$store.dispatch('setIsCartItem', false)
      this.$store.commit('SET_IS_OPTION', false)
      this.$store.dispatch('setWorkingMenuItem', clone)
      this.$store.dispatch('openMenuItemDialog')
    },
    async addAiRecommendation(item) {
      // Needs modifiers -> open the normal item detail page instead of adding blind.
      if (this.aiRecNeedsConfig(item)) {
        this.openAiRecommendationDialog(item)
        return
      }
      const real = this.findRealMenuItemByCode(item.code)
      if (!real) return
      // CART_ADD_ITEM expects a cart line, not a raw menu item (it reads
      // itemAttributeList.length) -- same shape menuItem.vue's quick add builds.
      const clone = {
        price: this.aiRecPrice(real),
        code: real.code,
        name: real.name,
        itemAttributeList: [],
        qty: real.minCardinality || 1,
        raw: cloneDeep(real)
      }
      // No re-request: the getter hides the added pick, and shows it again if it's removed
      // (see Cart.vue's aiCartRecommendationsCartKey watcher).
      await this.addToCart(clone)
    }
  }
}
</script>

<style scoped>
.ai-cart-recs-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 15px 8px;
}
.ai-cart-recs-title {
  font-weight: 700;
  font-size: 15px;
}
.ai-cart-recs-subtitle {
  font-size: 12px;
  color: #777;
  text-align: right;
}
.ai-cart-recs-list {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 2px 15px 8px;
}
.ai-cart-rec-card {
  flex: 0 0 136px;
  border: 1px solid #ddd;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  background: #fff;
}
.ai-cart-rec-card .ai-cart-rec-pic img {
  border-radius: 5px 5px 0 0;
}
.ai-cart-rec-pic {
  position: relative;
  height: 96px;
  background: #f4f4f4;
  /* Lets the add button hang past the image onto the body edge, as in the design. */
  overflow: visible;
}
.ai-cart-rec-pic img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.ai-cart-rec-pic-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 5px 5px 0 0;
}
.ai-cart-rec-add.v-btn {
  position: absolute;
  right: 6px;
  /* Straddles the image/body boundary, as in the design mockup. */
  bottom: -14px;
  margin: 0;
  height: 28px;
  width: 28px;
  background: #fff;
  border: 1px solid #1e9d84;
  color: #1e9d84;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
}
.ai-cart-rec-body {
  display: flex;
  flex-direction: column;
  /* Fill the card so margin-top:auto on the price pins it to the same bottom line on
     every card, however many lines the name/reason take. */
  flex: 1;
  min-height: 92px;
  padding: 6px 8px 8px;
  text-align: left;
}
/* Same pill as the menu cards (menuItemCard/menuItemCardCustom). Defined here too: those
   styles only load with the menu page, and checkout can be opened directly. */
.ai-tag-pill {
  display: inline-block;
  background-color: #6a1b9a;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 4px;
  vertical-align: middle;
}
/* Flex items stretch by default -- keep the tag pill its own width, as in the design,
   with room before the name. */
.ai-cart-rec-body .ai-tag-pill {
  align-self: flex-start;
  margin-bottom: 6px;
}
.ai-cart-rec-name {
  font-weight: 700;
  font-size: 13px;
}
.ai-cart-rec-reason {
  font-size: 12px;
  color: #777;
  /* 2 lines max with an ellipsis, per the design -- a long reason otherwise wraps to
     5+ lines and makes every card in the row taller. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ai-cart-rec-price {
  margin-top: auto;
  font-size: 12px;
  font-weight: 700;
}
/* Refreshing after an add: the current picks stay readable, just muted, so the row
   keeps its height and position instead of flashing skeletons on every add. */
.ai-cart-recs-refreshing {
  opacity: 0.55;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

/* Entry animation for the row arriving after the AI call returns. Enter only: on leave
   the block should go immediately (it's being hidden because it has nothing to show,
   and fading out slowly would draw attention to exactly the disappearance we don't
   want read as a bug). */
.ai-cart-recs-fade-enter-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.ai-cart-recs-fade-enter {
  opacity: 0;
  transform: translateY(8px);
}
.ai-cart-recs-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  .ai-cart-recs-fade-enter-active {
    transition: none;
  }
  .ai-cart-recs-fade-enter {
    opacity: 1;
    transform: none;
  }
}
</style>
